import { useState } from "react";
import {
  FileImage,
  Sparkles,
  Crop as CropIcon,
  UploadCloud,
  Users,
  Eye,
  CheckCircle2,
} from "lucide-react";
import {
  Avatar,
  AvatarGroup,
  Image,
  FileUpload,
  type UploadedFileItem,
  ImageUpload,
  type ImageUploadValue,
  Badge,
  Button,
} from "@/shared/ui";

export function TestUiCoreMediaShowcase() {
  // 1. Image Upload state with live preview & crop
  const [avatarImage, setAvatarImage] = useState<string | null>(
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&auto=format&fit=crop&q=80"
  );
  const [productImage, setProductImage] = useState<string | null>(null);

  // 2. File Upload Dropzone demo list
  const [fileList, setFileList] = useState<UploadedFileItem[]>([
    {
      id: "f-1",
      name: "Chung_tu_kiem_nghiem_lo_ca_tra_LK-2026.pdf",
      size: 2450000,
      type: "application/pdf",
      status: "success",
    },
    {
      id: "f-2",
      name: "Bang_ke_chi_tiet_nguyen_vat_lieu_T09.xlsx",
      size: 615000,
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      status: "success",
    },
    {
      id: "f-3",
      name: "Anh_chup_tem_mac_bao_bi_IQF.jpg",
      size: 1820000,
      type: "image/jpeg",
      status: "uploading",
      progress: 65,
    },
  ]);

  return (
    <div className="rounded-control border border-border bg-surface p-5 shadow-xs space-y-6">
      {/* Header */}
      <div className="border-b border-border/60 pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <FileImage className="size-5 text-primary" />
              Bộ Core Components Hình Ảnh, Cắt Ảnh & Upload File (Media Suite)
            </h3>
            <p className="mt-1 mb-0 text-caption text-text-muted">
              Chuẩn hóa toàn diện: <strong>Avatar & Group</strong> (tự tính tên viết tắt), <strong>Image & Lightbox</strong> (loading skeleton, zoom/xoay), <strong>FileUpload Dropzone</strong> (kéo thả tài liệu) và <strong>ImageUpload + ImageCropper</strong> (cắt ảnh bằng Canvas HTML5).
            </p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <Badge tone="success" label="Mới: React 19 Canvas" />
            <Badge tone="primary" label="@/shared/ui" />
          </div>
        </div>
      </div>

      {/* Grid: 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Column 1: Avatars & Safe Image Loader */}
        <div className="space-y-6">
          {/* 1. Avatar & AvatarGroup */}
          <div className="rounded-control border border-border/70 bg-bg/40 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-body font-bold text-text flex items-center gap-1.5">
                <Users className="size-4 text-primary" />
                1. Avatar & Nhóm Người Dùng (AvatarGroup)
              </span>
              <Badge tone="neutral" label="Initials + Presence" />
            </div>

            {/* Sizes & Initials */}
            <div className="space-y-2">
              <span className="text-caption text-text-muted block">
                Kích thước từ xs đến 2xl, tự tính Initials khi không có ảnh:
              </span>
              <div className="flex flex-wrap items-end gap-3">
                <Avatar size="xs" name="Trần Văn An" status="online" />
                <Avatar size="sm" name="Lê Thị Bình" status="busy" />
                <Avatar
                  size="md"
                  src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80"
                  name="Phạm Hồng Cẩm"
                  status="online"
                />
                <Avatar size="lg" name="Đặng Quốc Dũng" status="away" />
                <Avatar
                  size="xl"
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80"
                  name="Nguyễn Hữu Em"
                  status="offline"
                />
                <Avatar size="2xl" name="Ban Giám Đốc" shape="rounded" />
              </div>
            </div>

            {/* Avatar Group */}
            <div className="space-y-1.5 pt-2 border-t border-border/50">
              <span className="text-caption text-text-muted block">
                AvatarGroup hiển thị danh sách cộng tác viên (+4):
              </span>
              <AvatarGroup max={4} total={8} size="md">
                <Avatar
                  name="Admin"
                  src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80"
                />
                <Avatar name="Trần Mai" />
                <Avatar
                  name="Phạm Quân"
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80"
                />
                <Avatar name="Lê Hùng" />
              </AvatarGroup>
            </div>
          </div>

          {/* 2. Image with Lightbox Preview */}
          <div className="rounded-control border border-border/70 bg-bg/40 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-body font-bold text-text flex items-center gap-1.5">
                <Eye className="size-4 text-primary" />
                2. Image Component & Phóng To (Lightbox)
              </span>
              <Badge tone="primary" label="Bấm ảnh để phóng to" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-caption text-text-muted block mb-1.5">
                  Ảnh sản phẩm (Tỷ lệ 16:9 + Zoom):
                </span>
                <Image
                  src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&auto=format&fit=crop&q=80"
                  alt="Dây chuyền chế biến cá tra TM Food"
                  previewTitle="Dây chuyền fillet IQF tự động — Nhà máy số 2"
                  aspectRatio="video"
                  previewable
                  className="w-full"
                />
              </div>

              <div>
                <span className="text-caption text-text-muted block mb-1.5">
                  Trường hợp ảnh lỗi link (Error Fallback):
                </span>
                <Image
                  src="https://invalid-broken-url-example.com/broken.jpg"
                  alt="Ảnh không tồn tại"
                  aspectRatio="video"
                  previewable
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Column 2: Upload Suite (ImageUpload with Crop + FileUpload Dropzone) */}
        <div className="space-y-6">
          {/* 3. ImageUpload with Integrated Cropper */}
          <div className="rounded-control border border-border/70 bg-bg/40 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-body font-bold text-text flex items-center gap-1.5">
                <CropIcon className="size-4 text-primary" />
                3. Tải Lên & Cắt Ảnh Trực Tuyến (ImageUpload + Cropper)
              </span>
              <Badge tone="success" label="Canvas HTML5 Tích Hợp" />
            </div>

            <div className="space-y-4">
              {/* Avatar Uploader with 1:1 Crop */}
              <ImageUpload
                label="Ảnh đại diện nhân sự / Chuyên viên QA (Cắt tròn 1:1)"
                value={avatarImage}
                onChange={(val) => setAvatarImage(val ? val.dataUrl : null)}
                cropAspectRatio="1:1"
                shape="circle"
                size="md"
                hint="Bấm vào nút 'Cắt ảnh' để thử công cụ xoay, phóng to và chọn góc khuôn mặt."
              />

              {/* Product/Logo Uploader with 4:3 Crop */}
              <div className="pt-3 border-t border-border/50">
                <ImageUpload
                  label="Logo pháp nhân / Ảnh mẫu bao bì sản phẩm (Cắt 4:3)"
                  value={productImage}
                  onChange={(val) => setProductImage(val ? val.dataUrl : null)}
                  cropAspectRatio="4:3"
                  shape="rounded"
                  size="md"
                  hint="Hỗ trợ tải ảnh từ máy, tự động mở cửa sổ căn chỉnh trước khi lưu."
                />
              </div>
            </div>
          </div>

          {/* 4. FileUpload (Dropzone) */}
          <div className="rounded-control border border-border/70 bg-bg/40 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-body font-bold text-text flex items-center gap-1.5">
                <UploadCloud className="size-4 text-primary" />
                4. Kéo Thả Tệp Tin Chứng Từ (FileUpload Dropzone)
              </span>
              <Badge tone="neutral" label="Đa định dạng" />
            </div>

            <FileUpload
              files={fileList}
              onChange={setFileList}
              multiple
              title="Kéo thả tài liệu kiểm nghiệm / COA vào đây"
              hint="Hỗ trợ PDF, Excel, Word, Hình ảnh, Zip (tối đa 25 MB/tệp)."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
